import { computed, Injectable, signal } from '@angular/core';
import en from '../i18n/en.json';
import fi from '../i18n/fi.json';
import type { I18nKey, I18nParams, Translations } from '../i18n/i18n.types';
import {
  normalizeLanguage,
  resolveInitialLanguage,
  STORED_LANGUAGE_KEY,
  type Language,
} from '../i18n/language';

export type { Language };

const TRANSLATIONS: Record<Language, Translations> = { fi, en };

@Injectable({ providedIn: 'root' })
export class I18nService {
  /** Resolved once at construction, so the first render is already in the right
   *  language. An explicit `?lang=` deep link overrides it via
   *  {@link applyUrlLanguage}. */
  readonly language = signal<Language>(this.resolveInitialLanguage());
  private readonly messages = computed<Translations>(() => TRANSLATIONS[this.language()]);

  constructor() {
    this.syncDocumentLang(this.language());
  }

  /** Set the UI language and remember it across visits. */
  setLanguage(lang: Language): void {
    this.language.set(lang);
    this.persistLanguage(lang);
    this.syncDocumentLang(lang);
  }

  /** Apply an explicit `?lang=`/`?language=` deep-link hint, overriding the
   *  persisted/browser default and remembering it. A no-op for an unrecognized
   *  value, so a stray hint can't knock the funnel off the customer's language. */
  applyUrlLanguage(raw: string | null | undefined): void {
    const lang = normalizeLanguage(raw);
    if (lang) this.setLanguage(lang);
  }

  t(key: I18nKey, params?: I18nParams): string {
    const raw = this.messages()[key] ?? key;
    if (!params) return raw;
    return raw.replace(/\{(\w+)\}/g, (_, name) => {
      const value = params[name];
      return value === undefined ? `{${name}}` : String(value);
    });
  }

  /**
   * Browser preference is deliberately NOT consulted: the funnel's market is
   * Finnish, so it starts in Finnish unless the customer has made an explicit
   * choice: a `?lang=` link, or a pick from the switcher (in every step's top
   * bar) made earlier and persisted.
   *
   * `resolveInitialLanguage` in `language.ts` still accepts a `browserLanguages`
   * list (`navigator.languages`) should that decision change.
   */
  private resolveInitialLanguage(): Language {
    return resolveInitialLanguage({ storedChoice: this.readStoredLanguage() });
  }

  private readStoredLanguage(): string | null {
    try {
      return localStorage.getItem(STORED_LANGUAGE_KEY);
    } catch {
      // localStorage can throw in private mode / when storage is blocked.
      return null;
    }
  }

  private persistLanguage(lang: Language): void {
    try {
      localStorage.setItem(STORED_LANGUAGE_KEY, lang);
    } catch {
      // Best-effort: a blocked store just means no cross-visit memory.
    }
  }

  private syncDocumentLang(lang: Language): void {
    try {
      document.documentElement.lang = lang;
    } catch {
      // No document (non-browser context) — nothing to sync.
    }
  }
}
